/**
 * Cálculos derivados del módulo `/usage`.
 *
 * Se aplican en lectura y **nunca se almacenan**, igual que el resto del panel:
 * un cambio en los insumos no puede dejar un derivado desincronizado.
 *
 * Dos reglas gobiernan todo el archivo:
 *
 * - **Falta un insumo → `null`.** Nunca cero. Un cero se lee como una medición
 *   ("esta cuenta no consumió") y lo que pasa es otra cosa ("no sabemos cuánto
 *   consumió"). La UI muestra "Sin dato" con la acción de cargarlo.
 * - **Todo porcentaje publica su base.** Por eso estas funciones devuelven
 *   objetos y no números sueltos: el `base` que acompaña al `pct` es lo que
 *   permite recalcular el número a mano y lo que la pantalla tiene que mostrar
 *   al lado.
 */

import type {
  CadenaActivacion,
  Participacion,
  ProyeccionAgotamiento,
  QuemaPromedio,
  QuemaPunto,
  Variacion,
} from './usage.js'
import { MESES_QUEMA_PROMEDIO } from './usage.js'

const MS_POR_DIA = 86_400_000

/** Días promedio de un mes. Aproximación deliberada y declarada. */
const DIAS_POR_MES = 30

function redondear(n: number, decimales: number): number {
  const f = 10 ** decimales
  return Math.round(n * f) / f
}

/**
 * Quema promedio de créditos de los últimos `meses` meses con dato.
 *
 * Promedia **solo los meses medidos**, no los del calendario: si de los últimos
 * tres solo dos tienen medición, el promedio es sobre esos dos y `meses` lo
 * dice. Contar un mes sin dato como cero bajaría la quema y correría la fecha de
 * agotamiento hacia adelante — justo el error que deja a un cliente sin servicio
 * sin aviso.
 *
 * La serie entra del más viejo al más nuevo; se toman los últimos.
 */
export function quemaPromedioCreditos(
  serie: readonly { periodo: string; creditos: number | null }[],
  meses: number = MESES_QUEMA_PROMEDIO,
): QuemaPromedio | null {
  if (meses <= 0) return null

  const conDato: QuemaPunto[] = serie
    .slice(-meses)
    .filter((p): p is QuemaPunto => p.creditos !== null)

  if (conDato.length === 0) return null

  const total = conDato.reduce((acc, p) => acc + p.creditos, 0)
  return {
    promedioMensual: redondear(total / conDato.length, 2),
    meses: conDato.length,
    base: conDato,
  }
}

/**
 * Fecha proyectada de agotamiento del saldo de créditos.
 *
 * Es la que dispara la conversación comercial antes de que el servicio se corte.
 * Devuelve `null` cuando falta el saldo, falta la quema, o **la quema es cero o
 * negativa**: sin consumo el saldo no se agota nunca, y una fecha inventada a
 * diez años sería peor que no mostrar nada.
 *
 * Un saldo ya en cero o negativo proyecta `dias: 0` y la fecha de hoy: está
 * agotado, y eso sí es un dato.
 */
export function fechaProyectadaDeAgotamiento(input: {
  saldo: number | null
  quemaMensual: number | null
  desde: Date
}): ProyeccionAgotamiento | null {
  const { saldo, quemaMensual, desde } = input
  if (saldo === null || quemaMensual === null) return null
  if (!Number.isFinite(saldo) || !Number.isFinite(quemaMensual)) return null
  if (quemaMensual <= 0) return null
  if (Number.isNaN(desde.getTime())) return null

  const meses = Math.max(0, saldo / quemaMensual)
  const dias = Math.round(meses * DIAS_POR_MES)
  const fecha = new Date(desde.getTime() + dias * MS_POR_DIA)

  return {
    fecha: fecha.toISOString().slice(0, 10),
    dias,
    meses: redondear(meses, 2),
    base: {
      saldo,
      quemaMensual,
      desde: desde.toISOString(),
    },
  }
}

/**
 * Variación contra un período de referencia, con la base publicada.
 *
 * `pct` queda en `null` cuando la base es cero —no hay porcentaje contra cero—
 * pero el `delta` absoluto se publica igual porque sí es verdadero. La pantalla
 * dice "+1.200 créditos desde 0" y no "+∞%".
 */
export function variacionContra(input: {
  actual: number | null
  anterior: number | null
  periodoBase: string
}): Variacion | null {
  const { actual, anterior, periodoBase } = input
  if (actual === null || anterior === null) return null

  const delta = actual - anterior
  return {
    actual,
    delta: redondear(delta, 2),
    pct: anterior === 0 ? null : redondear((delta / anterior) * 100, 1),
    base: { periodo: periodoBase, valor: anterior },
  }
}

/**
 * Costo de servir por agente.
 *
 * El divisor son los agentes **activos**, no los contratados: repartir el costo
 * entre agentes que no produjeron ninguna traza daría un costo por agente más
 * bajo del real y haría parecer eficiente a la cuenta que peor está.
 */
export function costoPorAgente(input: {
  costoUsd: number | null
  agentesActivos: number | null
}): number | null {
  const { costoUsd, agentesActivos } = input
  if (costoUsd === null || agentesActivos === null || agentesActivos <= 0) {
    return null
  }
  return redondear(costoUsd / agentesActivos, 4)
}

/** Costo de servir por traza. null si no hubo trazas: no es cero, es indefinido. */
export function costoPorTraza(input: {
  costoUsd: number | null
  trazas: number | null
}): number | null {
  const { costoUsd, trazas } = input
  if (costoUsd === null || trazas === null || trazas <= 0) return null
  return redondear(costoUsd / trazas, 6)
}

/**
 * Participación de una parte sobre el total, con el total publicado.
 *
 * `null` cuando el total es cero o falta: una participación sobre nada no es 0%,
 * es indefinida.
 */
export function participacion(input: {
  parte: number | null
  total: number | null
}): Participacion | null {
  const { parte, total } = input
  if (parte === null || total === null || total === 0) return null
  return {
    pct: redondear((parte / total) * 100, 1),
    base: { parte, total },
  }
}

/**
 * Reparto por modelo: la participación de cada ítem sobre el total de la lista.
 *
 * Calcula el total una sola vez y lo publica en cada fila, que es lo que exige
 * la regla de números derivables: la columna "% del gasto" tiene que poder
 * verificarse sin sumar la tabla a mano.
 */
export function participacionesSobreElTotal<T>(
  items: readonly T[],
  valorDe: (item: T) => number | null,
): { item: T; participacion: Participacion | null }[] {
  const total = items.reduce((acc, item) => acc + (valorDe(item) ?? 0), 0)
  return items.map((item) => ({
    item,
    participacion: participacion({ parte: valorDe(item), total }),
  }))
}

/**
 * El tramo "activo" de la cadena de activación: contratado → instalado → activo.
 *
 * Es la aportación central del módulo. Clientes mide los dos primeros tramos y
 * cuenta como éxito a un agente instalado; acá se agrega el tercero, donde
 * **activo significa con al menos una traza en el período**, y la brecha de uso
 * (`instalados − activos`) queda a la vista.
 *
 * Devuelve `null` si falta cualquiera de los tres insumos: una cadena a la que
 * le falta un eslabón no se puede dibujar sin mentir sobre el que falta.
 *
 * Los valores se saturan hacia abajo (`activos ≤ instalados ≤ contratados`)
 * porque una cadena creciente sería un dato imposible: un agente no puede estar
 * activo sin estar instalado. Cuando llega así, es que las dos fuentes —la
 * plataforma y Langfuse— no cierran, y el tramo se recorta en vez de dibujar una
 * brecha negativa que la pantalla no sabría explicar.
 */
export function cadenaDeActivacion(input: {
  contratados: number | null
  instalados: number | null
  activos: number | null
}): CadenaActivacion | null {
  const { contratados, instalados, activos } = input
  if (contratados === null || instalados === null || activos === null) return null

  const c = Math.max(0, contratados)
  const i = Math.min(c, Math.max(0, instalados))
  const a = Math.min(i, Math.max(0, activos))

  return {
    contratados: c,
    instalados: i,
    activos: a,
    brechaInstalacion: c - i,
    brechaUso: i - a,
    pctInstalado: c === 0 ? null : redondear((i / c) * 100, 1),
    pctActivoSobreInstalados: i === 0 ? null : redondear((a / i) * 100, 1),
    pctActivoSobreContratados: c === 0 ? null : redondear((a / c) * 100, 1),
  }
}

/**
 * Margen de una cuenta. **Solo se sirve a admin** (ver la excepción de RBAC).
 *
 * Vive acá y no en el repo porque es un derivado como cualquier otro y tiene que
 * publicar su base: `precio − costo`, con los dos números al lado.
 */
export function margenDeCuenta(input: {
  precioUsd: number | null
  costoUsd: number | null
}): { margenUsd: number | null; margenPct: number | null } {
  const { precioUsd, costoUsd } = input
  if (precioUsd === null || costoUsd === null) {
    return { margenUsd: null, margenPct: null }
  }
  const margenUsd = redondear(precioUsd - costoUsd, 2)
  return {
    margenUsd,
    margenPct: precioUsd === 0 ? null : redondear((margenUsd / precioUsd) * 100, 1),
  }
}

/**
 * Días desde la última señal de vida de un agente.
 *
 * Si nunca tuvo actividad, cuenta desde el despliegue: es el "cuántos días lleva
 * así" del bloque de agentes instalados sin uso. Sin ninguna de las dos fechas
 * devuelve `null` — un 0 diría "se desplegó hoy", que puede ser falso.
 */
export function diasSinUso(input: {
  ultimaActividadEn: string | null
  desplegadoEn: string | null
  ahora: Date
}): number | null {
  const { ultimaActividadEn, desplegadoEn, ahora } = input
  const referencia = ultimaActividadEn ?? desplegadoEn
  if (referencia === null) return null

  const desde = new Date(referencia)
  if (Number.isNaN(desde.getTime())) return null

  return Math.max(0, Math.floor((ahora.getTime() - desde.getTime()) / MS_POR_DIA))
}
